/**
 * A level's items as the C64 sets them up (level-setup.s setup_level_data $2B31): the food slot
 * (L_2BB0), the power-up slot - a door ($2BDB) or the chosen power-up (L_2BFC) - their timers ($2C5B)
 * and places (D_2C8C); level 100's two lightning potions ($2B98, D_7F83).
 */

import { rngOf } from '../random';
import { chooseFood, choosePowerup, doorFor } from './choice';
import { LIGHTNING_POTION } from './powerups';
import { emptySlot, newSlot, showSlot } from './slots';
import { initialItemSystem, type ItemSystem } from './state';
import { FOOD_DELAY, FOOD_DELAY_MASK, POWERUP_DELAY, POWERUP_DELAY_MASK } from './tuning';
import type { World } from '../types';

/** The world's item state, made on first use. */
export function systemOf(world: World): ItemSystem {
  if (!world.itemSystem) world.itemSystem = initialItemSystem();
  return world.itemSystem;
}

/**
 * Level 100 ($2B98): no food roll, no door; $58 cleared through D_2BBD, and D_7F83 puts both slots on
 * screen as lightning potions (slot 0 through the food tables at index $51, which read on into the
 * power-up tables' entry 34). The per-second timers never run there, so they stay.
 */
export function placePotions(world: World, sys: ItemSystem): void {
  const it = world.level.items;
  for (const [slot, tile] of [[sys.food, it.points], [sys.powerup, it.powerup]] as const) {
    if (slot.visible) continue;
    emptySlot(world, slot);
    Object.assign(slot, newSlot(LIGHTNING_POTION, 0, tile));
    showSlot(world, slot, 'powerup');
  }
}

export function setupLevelItems(world: World): void {
  const sys = initialItemSystem();
  world.itemSystem = sys;
  sys.ready = true;
  sys.frameMark = world.flow.frame;
  sys.seenState = [world.players[0].state, world.players[1].state];
  const carry = world.carry;
  if (world.level.n >= 100) {
    carry.foodBase = 0;
    placePotions(world, sys);
    return;
  }
  const rng = rngOf(world);
  const food = chooseFood(carry, rng);
  const door = doorFor(carry, world.levelIndex);
  const powerup = door >= 0 ? door : choosePowerup(world.levelIndex, rng);
  const foodTimer = FOOD_DELAY + (rng.int(256) & FOOD_DELAY_MASK);
  const powerupTimer = POWERUP_DELAY + (rng.int(256) & POWERUP_DELAY_MASK);
  sys.food = newSlot(food, foodTimer, world.level.items.points);
  sys.powerup = newSlot(powerup, powerupTimer, world.level.items.powerup);
}
